/**
 * SNAPSHOT INTERPOLATION
 *
 * Interpolação por buffer de snapshots com relógio sincronizado ao servidor —
 * o padrão de netcode usado por jogos de ação (Valve/Source, etc.):
 *
 * 1. Cada patch de estado do Colyseus chega com `state.serverTime` (relógio do
 *    servidor). Estimamos o offset servidor→cliente com suavização exponencial.
 * 2. Cada entidade remota mantém um buffer de snapshots {t, x, z, rot, turret}.
 * 3. A renderização acontece em `renderTime = serverNow - INTERP_DELAY`,
 *    interpolando linearmente entre os dois snapshots que cercam esse instante.
 *    Assim o movimento remoto é reproduzido com a MESMA velocidade que ocorreu,
 *    sem jitter, independente do framerate local ou da cadência de patches.
 * 4. Se o buffer secar (perda de pacotes), extrapola por até MAX_EXTRAP_MS
 *    usando a última velocidade conhecida; depois congela em vez de derrapar.
 */

export interface EntitySnapshot {
  t: number; // server time (ms)
  x: number;
  z: number;
  rotation: number;
  turretRotation: number;
}

export interface InterpolatedPose {
  x: number;
  z: number;
  rotation: number;
  turretRotation: number;
}

/** Atraso de renderização: ~2 patches de 33ms + folga para jitter. */
const INTERP_DELAY_MS = 100;
/** Extrapolação máxima quando faltam snapshots novos. */
const MAX_EXTRAP_MS = 250;
/** Descartar snapshots mais velhos que isso. */
const BUFFER_RETENTION_MS = 1000;
/** Gap acima disso (respawn/teleporte) não é interpolado — snap. */
const TELEPORT_DISTANCE = 8;

function lerpAngle(a: number, b: number, t: number): number {
  const diff = Math.atan2(Math.sin(b - a), Math.cos(b - a));
  return a + diff * t;
}

export class SnapshotInterpolation {
  private buffers = new Map<string, EntitySnapshot[]>();
  private clockOffsetMs: number | null = null; // serverTime - clientTime

  /**
   * Alimenta a estimativa de relógio com o serverTime do último patch.
   * Offset suavizado exponencialmente; saltos grandes (reconexão) fazem snap.
   */
  updateServerClock(serverTime: number) {
    if (!serverTime || !isFinite(serverTime)) return;
    const sample = serverTime - Date.now();
    if (this.clockOffsetMs === null || Math.abs(sample - this.clockOffsetMs) > 2000) {
      this.clockOffsetMs = sample;
    } else {
      // Convergência lenta evita que jitter de rede balance a timeline
      this.clockOffsetMs += (sample - this.clockOffsetMs) * 0.1;
    }
  }

  /** Tempo estimado atual do servidor. */
  serverNow(): number {
    return Date.now() + (this.clockOffsetMs ?? 0);
  }

  hasClock(): boolean {
    return this.clockOffsetMs !== null;
  }

  /** Registra um snapshot da entidade (chamado a cada patch de estado). */
  pushSnapshot(id: string, snap: EntitySnapshot) {
    let buffer = this.buffers.get(id);
    if (!buffer) {
      buffer = [];
      this.buffers.set(id, buffer);
    }

    const last = buffer[buffer.length - 1];
    // Ignorar duplicatas do mesmo tick (patches podem repetir valores)
    if (last && last.t === snap.t && last.x === snap.x && last.z === snap.z &&
        last.rotation === snap.rotation && last.turretRotation === snap.turretRotation) {
      return;
    }
    // Timeline deve ser monotônica
    if (last && snap.t <= last.t) {
      snap = { ...snap, t: last.t + 1 };
    }

    buffer.push(snap);

    // Podar snapshots antigos
    const cutoff = snap.t - BUFFER_RETENTION_MS;
    while (buffer.length > 2 && buffer[0].t < cutoff) {
      buffer.shift();
    }
  }

  remove(id: string) {
    this.buffers.delete(id);
  }

  clear() {
    this.buffers.clear();
    this.clockOffsetMs = null;
  }

  /**
   * Pose interpolada da entidade para o frame atual (null se sem dados).
   */
  getPose(id: string): InterpolatedPose | null {
    const buffer = this.buffers.get(id);
    if (!buffer || buffer.length === 0) return null;

    const renderTime = this.serverNow() - INTERP_DELAY_MS;

    // Antes do primeiro snapshot: usar o primeiro
    if (renderTime <= buffer[0].t || buffer.length === 1) {
      const s = buffer[0];
      return { x: s.x, z: s.z, rotation: s.rotation, turretRotation: s.turretRotation };
    }

    // Procurar o par [older, newer] que cerca renderTime
    for (let i = buffer.length - 1; i >= 1; i--) {
      const newer = buffer[i];
      const older = buffer[i - 1];
      if (renderTime >= older.t && renderTime <= newer.t) {
        const span = newer.t - older.t;
        const t = span > 0 ? (renderTime - older.t) / span : 1;

        // Respawn/teleporte: não interpolar através do mapa
        const dist = Math.hypot(newer.x - older.x, newer.z - older.z);
        if (dist > TELEPORT_DISTANCE) {
          const s = t < 0.5 ? older : newer;
          return { x: s.x, z: s.z, rotation: s.rotation, turretRotation: s.turretRotation };
        }

        return {
          x: older.x + (newer.x - older.x) * t,
          z: older.z + (newer.z - older.z) * t,
          rotation: lerpAngle(older.rotation, newer.rotation, t),
          turretRotation: lerpAngle(older.turretRotation, newer.turretRotation, t),
        };
      }
    }

    // renderTime além do snapshot mais novo: extrapolar com velocidade limitada
    const newest = buffer[buffer.length - 1];
    const prev = buffer[buffer.length - 2];
    const ahead = Math.min(renderTime - newest.t, MAX_EXTRAP_MS);
    const span = newest.t - prev.t;
    if (span <= 0 || ahead <= 0) {
      return { x: newest.x, z: newest.z, rotation: newest.rotation, turretRotation: newest.turretRotation };
    }
    const vx = (newest.x - prev.x) / span;
    const vz = (newest.z - prev.z) / span;
    // Não extrapolar teleportes
    if (Math.hypot(newest.x - prev.x, newest.z - prev.z) > TELEPORT_DISTANCE) {
      return { x: newest.x, z: newest.z, rotation: newest.rotation, turretRotation: newest.turretRotation };
    }
    return {
      x: newest.x + vx * ahead,
      z: newest.z + vz * ahead,
      rotation: newest.rotation,
      turretRotation: newest.turretRotation,
    };
  }
}

/** Instância global — timeline única por sala. */
export const snapshotInterpolation = new SnapshotInterpolation();
